import { Workspace } from '@prisma/client'
import { useMatches } from '@remix-run/react'
import { useMemo } from 'react'

import type { User } from '~/models/user.server'

import { getNameInitials, getUserInitials } from './lib/utils'
import { RootLoaderData } from './root'

const DEFAULT_REDIRECT = '/'

/**
 * This should be used any time the redirect path is user-provided
 * (Like the query string on our login/signup pages). This avoids
 * open-redirect vulnerabilities.
 * @param {string} to The redirect destination
 * @param {string} defaultRedirect The redirect to use if the to is unsafe.
 */
export function safeRedirect(
  to: FormDataEntryValue | string | null | undefined,
  defaultRedirect: string = DEFAULT_REDIRECT,
) {
  if (!to || typeof to !== 'string') {
    return defaultRedirect
  }

  if (!to.startsWith('/') || to.startsWith('//')) {
    return defaultRedirect
  }

  return to
}

/**
 * This base hook is used in other hooks to quickly search for specific data
 * across all loader data using useMatches.
 * @param {string} id The route id
 * @returns {JSON|undefined} The router data or undefined if not found
 */
export function useMatchesData<TData = Record<string, unknown>>(
  id: string,
): TData | undefined {
  const matchingRoutes = useMatches()
  const route = useMemo(
    () => matchingRoutes.find((route) => route.id === id),
    [matchingRoutes, id],
  )
  return route?.data as TData
}

function isWorkspace(workspace: unknown): workspace is Workspace {
  return (
    workspace != null &&
    typeof workspace === 'object' &&
    'id' in workspace &&
    'name' in workspace &&
    typeof workspace.name === 'string'
  )
}

export function useOptionalWorkspace() {
  const data = useMatchesData('root') as RootLoaderData
  if (!data || !isWorkspace(data.workspace)) {
    return undefined
  }
  return data.workspace
}

export function useOptionalUserWorkspaces() {
  const data = useMatchesData('root') as RootLoaderData
  if (!data || !data.user?.workspaces) {
    return undefined
  }
  return data.user.workspaces
}

/** Whether the in-app bug reporter should be offered (server-decided). */
export function useBugReportingEnabled(): boolean {
  const data = useMatchesData('root') as RootLoaderData
  return !!data?.bugReportingEnabled
}

export type UseWorkspace = ReturnType<typeof useWorkspace>
export function useWorkspace() {
  const maybeWorkspace = useOptionalWorkspace()
  if (!maybeWorkspace) {
    throw new Error(
      'No workspace found in root loader, but user is required by useWorkspace. If workspace is optional, try useOptionalWorkspace instead.',
    )
  }
  return maybeWorkspace
}

export const useWorkspaceInitials = () => {
  const workspace = useWorkspace()
  const initials = getNameInitials(workspace.name)
  if (initials.length < 2) {
    return workspace.name.substring(0, 2)
  }
  return initials
}

export function useWorkspaceUser() {
  const workspace = useWorkspace()
  const user = useUser()
  return user.workspaceUsers.find((x) => x.workspaceId === workspace.id)
}

function isUser(user: unknown): user is User {
  return (
    user != null &&
    typeof user === 'object' &&
    'email' in user &&
    typeof user.email === 'string'
  )
}

export function useOptionalUser() {
  const data = useMatchesData('root') as RootLoaderData
  if (!data || !isUser(data.user)) {
    return undefined
  }
  return data.user
}

export const getUserName = (
  user?: Pick<User, 'firstName' | 'lastName' | 'name' | 'email'>,
) => {
  return (
    `${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() ||
    user?.name ||
    user?.email
  )
}

export function useUserName() {
  const user = useOptionalUser()
  if (!user) {
    return 'Guest'
  }
  return getUserName(user)
}

export type UseUser = ReturnType<typeof useUser>
export function useUser() {
  const maybeUser = useOptionalUser()
  if (!maybeUser) {
    throw new Error(
      'No user found in root loader, but user is required by useUser. If user is optional, try useOptionalUser instead.',
    )
  }
  return maybeUser
}

export const useUserInitials = () => {
  const user = useUser()
  const initials = getUserInitials(user)

  return initials
}

export function validateEmail(email: unknown): email is string {
  const emailRE = /^([a-zA-Z0-9_\-.]+)@([a-zA-Z0-9_\-.]+)\.([a-zA-Z]{2,5})$/
  return typeof email === 'string' && email.length > 3 && emailRE.test(email)
}

export function isSSR(): boolean {
  return typeof window === 'undefined'
}
